import { useMemo } from 'react';

import { useAssetStalenessQuery, useCohortVersionQuery } from '@verantos-dev/data-access-cohorts';
import type { CohortBuilderDefinitionLegacy } from '@verantos-dev/types-cohorts';

import { useAdRouteParams } from './useAdRouteParams';
import { useSelectedSourceCohort } from './useSelectedSourceCohort';

// Legacy cohort versions sometimes wire `jsonValue` as a JSON string even
// though the type says object — parse defensively.
const parseCohortDefinition = (
  raw: CohortBuilderDefinitionLegacy | string | undefined
): CohortBuilderDefinitionLegacy | undefined => {
  if (!raw) return undefined;
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw) as CohortBuilderDefinitionLegacy;
  } catch {
    return undefined;
  }
};

export interface UseInheritedStudyPeriodResult {
  startDate: string | null;
  endDate: string | null;
  isSet: boolean;
  inheritedFromCohortName: string | null;
  isLoading: boolean;
  /** True when an analytical variable the cohort depends on has
   *  changed since the cohort was last saved/processed.
   *  TODO: cleanup with av_edit_while_in_use ff */
  isStale: boolean;
}

/**
 * Resolves the study period the dataset inherits from its selected source
 * cohort (the cohort version's overall study-period restriction). Shared by
 * the Study period section and the Source cohorts row so both read the same
 * (react-query-deduped) source.
 */
export const useInheritedStudyPeriod = (): UseInheritedStudyPeriodResult => {
  const { projectId } = useAdRouteParams();
  const { cohortProfileId, selectedCohort, isCohortListLoading } = useSelectedSourceCohort();
  const cohortVersionId = selectedCohort?.latestCohortVersionId ?? '';
  const inheritedFromCohortName = selectedCohort?.cohortName ?? null;

  // `cohortId` param actually takes the profile id — naming is inconsistent
  // across the codebase but the endpoint accepts it.
  const { data: versionData, isLoading: isVersionLoading } = useCohortVersionQuery({
    projectId,
    cohortId: cohortProfileId ?? '',
    cohortVersionId,
    enabled: Boolean(projectId && cohortProfileId && cohortVersionId),
  });

  const definition = useMemo(
    () => parseCohortDefinition(versionData?.jsonValue),
    [versionData?.jsonValue]
  );
  const studyPeriod = definition?.studyPeriodRestrictions?.overallStudyPeriodRestriction;
  const startDate = studyPeriod?.startDate ?? null;
  const endDate = studyPeriod?.endDate ?? null;
  const isSet = Boolean(startDate && endDate);

  const isLoading =
    Boolean(cohortProfileId) &&
    (isCohortListLoading || (Boolean(cohortVersionId) && isVersionLoading));

  // TODO: cleanup with asset_edit_while_in_use ff
  const { data: staleness } = useAssetStalenessQuery({
    kind: 'cohort',
    id: cohortVersionId,
    enabled: Boolean(cohortVersionId),
  });
  const isStale = staleness?.isStale ?? false;

  return { startDate, endDate, isSet, inheritedFromCohortName, isLoading, isStale };
};
